import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8002/api');

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [visits, setVisits] = useState([]);
  const [detailedVisits, setDetailedVisits] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [products, setProducts] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [editingUser, setEditingUser] = useState(null);
  const [editingVisit, setEditingVisit] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingEmailConfig, setEditingEmailConfig] = useState(null);
  const [viewingMessage, setViewingMessage] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showEmailConfigModal, setShowEmailConfigModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [userPasswords, setUserPasswords] = useState({}); // Store passwords temporarily for viewing

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, visitsRes, detailedRes, contactRes, productsRes, emailConfigRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/admin/visits`),
        axios.get(`${API_URL}/admin/visits/detailed`),
        axios.get(`${API_URL}/admin/contact-submissions`),
        axios.get(`${API_URL}/admin/products`),
        axios.get(`${API_URL}/admin/email-config`).catch(() => ({ data: { config: null } }))
      ]);
      setUsers(usersRes.data.users);
      setVisits(visitsRes.data.visits);
      setDetailedVisits(detailedRes.data.visits);
      setContactSubmissions(contactRes.data.submissions);
      setProducts(productsRes.data.products);
      setEmailConfig(emailConfigRes.data.config);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser({ email: '', password: '', is_admin: false });
    setShowUserModal(true);
    setShowPassword(false);
    setError('');
    setSuccess('');
  };

  const handleEditUser = (user) => {
    setEditingUser({ ...user, password: userPasswords[user.id] || '' }); // Include stored password if available
    setShowUserModal(true);
    setShowPassword(false);
    setError('');
    setSuccess('');
  };

  const handleSaveUser = async () => {
    try {
      setError('');
      
      // Validate required fields
      if (!editingUser.email) {
        setError('Email is required');
        return;
      }
      
      // If creating new user, password is required
      if (!editingUser.id && !editingUser.password) {
        setError('Password is required for new users');
        return;
      }
      
      if (editingUser.password && editingUser.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
      
      let response;
      let savedPassword = editingUser.password; // Store password for viewing
      
      if (editingUser.id) {
        // Update existing user
        response = await axios.put(`${API_URL}/admin/users/${editingUser.id}`, {
          email: editingUser.email,
          is_admin: editingUser.is_admin,
          password: editingUser.password || undefined
        });
        const updatedUser = response.data.user;
        setUsers(users.map(u => u.id === editingUser.id ? updatedUser : u));
        
        // Store password if it was changed
        if (editingUser.password) {
          setUserPasswords({ ...userPasswords, [updatedUser.id]: savedPassword });
        }
        setSuccess('User updated successfully');
      } else {
        // Create new user using register endpoint
        response = await axios.post(`${API_URL}/register`, {
          email: editingUser.email,
          password: editingUser.password
        });
        
        let newUser = response.data.user;
        
        // If admin checkbox is checked, update the user to be admin
        if (editingUser.is_admin) {
          await axios.put(`${API_URL}/admin/users/${newUser.id}`, {
            is_admin: true
          });
          newUser.is_admin = true;
        }
        
        setUsers([newUser, ...users]);
        
        // Store password for viewing
        setUserPasswords({ ...userPasswords, [newUser.id]: savedPassword });
        
        setSuccess('User created successfully');
      }
      
      setShowUserModal(false);
      setEditingUser(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      setSuccess('User deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleEditVisit = (visit) => {
    setEditingVisit({ ...visit });
    setShowVisitModal(true);
    setError('');
    setSuccess('');
  };

  const handleSaveVisit = async () => {
    try {
      setError('');
      const response = await axios.put(`${API_URL}/admin/visits/${editingVisit.id}`, {
        app_url: editingVisit.app_url,
        user_id: editingVisit.user_id || null,
        visited_at: editingVisit.visited_at
      });
      
      setDetailedVisits(detailedVisits.map(v => v.id === editingVisit.id ? response.data.visit : v));
      setShowVisitModal(false);
      setEditingVisit(null);
      setSuccess('Visit updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchData(); // Refresh aggregated stats
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update visit');
    }
  };

  const handleDeleteVisit = async (visitId) => {
    if (!window.confirm('Are you sure you want to delete this visit?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/visits/${visitId}`);
      setDetailedVisits(detailedVisits.filter(v => v.id !== visitId));
      setSuccess('Visit deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchData(); // Refresh aggregated stats
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete visit');
    }
  };

  const handleDeleteContactSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this contact submission?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/contact-submissions/${submissionId}`);
      setContactSubmissions(contactSubmissions.filter(s => s.id !== submissionId));
      setSuccess('Contact submission deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete contact submission');
    }
  };

  // Filter data based on search query
  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.id.toString().includes(searchQuery) ||
    (user.is_admin ? 'admin' : 'user').includes(searchQuery.toLowerCase())
  );

  const filteredVisits = visits.filter(visit =>
    visit.app_url.toLowerCase().includes(searchQuery.toLowerCase()) ||
    visit.visit_count.toString().includes(searchQuery) ||
    visit.unique_visitors.toString().includes(searchQuery)
  );

  const filteredDetailedVisits = detailedVisits.filter(visit =>
    visit.app_url.toLowerCase().includes(searchQuery.toLowerCase()) ||
    visit.id.toString().includes(searchQuery) ||
    (visit.user_email || 'anonymous').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredContactSubmissions = contactSubmissions.filter(submission =>
    submission.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    submission.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    submission.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (submission.user_email || 'anonymous').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (product.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (product.url || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (product.status || 'coming soon').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddProduct = () => {
    setEditingProduct({ name: '', description: '', image_url: '', url: '', status: 'coming soon' });
    setShowProductModal(true);
    setError('');
    setSuccess('');
  };

  const handleEditProduct = (product) => {
    setEditingProduct({ ...product });
    setShowProductModal(true);
    setError('');
    setSuccess('');
  };

  const handleSaveProduct = async () => {
    try {
      setError('');
      if (!editingProduct.name || !editingProduct.url) {
        setError('Product name and URL are required');
        return;
      }

      if (editingProduct.id) {
        // Update existing product
        const response = await axios.put(`${API_URL}/admin/products/${editingProduct.id}`, {
          name: editingProduct.name,
          description: editingProduct.description || '',
          image_url: editingProduct.image_url || '',
          url: editingProduct.url,
          status: editingProduct.status || 'coming soon'
        });
        setProducts(products.map(p => p.id === editingProduct.id ? response.data.product : p));
        setSuccess('Product updated successfully');
      } else {
        // Create new product
        const response = await axios.post(`${API_URL}/admin/products`, {
          name: editingProduct.name,
          description: editingProduct.description || '',
          image_url: editingProduct.image_url || '',
          url: editingProduct.url,
          status: editingProduct.status || 'coming soon'
        });
        setProducts([response.data.product, ...products]);
        setSuccess('Product added successfully');
      }
      setShowProductModal(false);
      setEditingProduct(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/products/${productId}`);
      setProducts(products.filter(p => p.id !== productId));
      setSuccess('Product deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete product');
    }
  };

  const handleEditEmailConfig = () => {
    if (emailConfig) {
      setEditingEmailConfig({ 
        ...emailConfig, 
        email_password: '',
        use_custom_smtp: !!(emailConfig.smtp_host)
      });
    } else {
      setEditingEmailConfig({
        email_user: '',
        contact_email: '',
        email_password: '',
        smtp_host: null,
        smtp_port: null,
        smtp_secure: false,
        use_custom_smtp: false
      });
    }
    setShowEmailConfigModal(true);
    setShowEmailPassword(false);
    setError('');
    setSuccess('');
  };

  const handleSaveEmailConfig = async () => {
    try {
      setError('');
      // Prepare config for saving - remove use_custom_smtp flag and handle smtp_host
      const configToSave = { ...editingEmailConfig };
      delete configToSave.use_custom_smtp;
      
      // If not using custom SMTP, set smtp_host to null
      if (!editingEmailConfig.use_custom_smtp) {
        configToSave.smtp_host = null;
        configToSave.smtp_port = null;
        configToSave.smtp_secure = false;
      }
      
      // Handle empty password - if password is empty string, don't send it (keep existing)
      if (configToSave.email_password === '' || configToSave.email_password === null) {
        delete configToSave.email_password;
      }
      
      const response = await axios.put(`${API_URL}/admin/email-config`, configToSave);
      // Fetch fresh config from server to ensure we have the latest data
      const configRes = await axios.get(`${API_URL}/admin/email-config`);
      const updatedConfig = configRes.data.config;
      setEmailConfig(updatedConfig);
      setSuccess(response.data.message || 'Email configuration saved successfully');
      // Clear any errors
      setError('');
      // Close modal immediately after successful save
      setShowEmailConfigModal(false);
      setEditingEmailConfig(null);
      setShowEmailPassword(false);
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Error saving email config:', error.response?.data);
      let errorMsg = error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || error.response?.data?.errors?.[0]?.message || 'Failed to save email configuration';
      const helpText = error.response?.data?.help;
      if (helpText) {
        errorMsg = `${errorMsg}\n\n${helpText}`;
      }
      setError(errorMsg);
    }
  };

  const handleTestEmailConfig = async () => {
    try {
      setTestingEmail(true);
      setError('');
      setSuccess('');
      // First save if we're in the modal and there are changes, then test
      if (showEmailConfigModal && editingEmailConfig) {
        // Save configuration first if there are changes
        const configToSave = { ...editingEmailConfig };
        delete configToSave.use_custom_smtp;
        
        // If not using custom SMTP, set smtp_host to null
        if (!editingEmailConfig.use_custom_smtp) {
          configToSave.smtp_host = null;
          configToSave.smtp_port = null;
          configToSave.smtp_secure = false;
        }
        
        // Handle empty password - if password is empty string, don't send it (keep existing)
        if (configToSave.email_password === '' || configToSave.email_password === null) {
          delete configToSave.email_password;
        }
        
        await axios.put(`${API_URL}/admin/email-config`, configToSave);
        const configRes = await axios.get(`${API_URL}/admin/email-config`);
        setEmailConfig(configRes.data.config);
      }
      const response = await axios.post(`${API_URL}/admin/email-config/test`);
      setSuccess(response.data.message || 'Test email sent successfully! Check your inbox.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error testing email config:', error.response?.data);
      let errorMsg = error.response?.data?.error || 'Failed to send test email';
      const helpText = error.response?.data?.help;
      if (helpText) {
        errorMsg = `${errorMsg}\n\n${helpText}`;
      }
      setError(errorMsg);
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      <section className="section">
        <div className="container">
          <h1 className="section-title">Admin <span className="accent">Dashboard</span></h1>
          
          {error && (
            <div className="alert alert-error dismissible-alert">
              <span>{error}</span>
              <button 
                className="alert-close-btn" 
                onClick={() => setError('')}
                aria-label="Close error"
              >
                ×
              </button>
            </div>
          )}
          {success && (
            <div className="alert alert-success dismissible-alert">
              <span>{success}</span>
              <button 
                className="alert-close-btn" 
                onClick={() => setSuccess('')}
                aria-label="Close success message"
              >
                ×
              </button>
            </div>
          )}
          
          {/* Search Bar */}
          <div className="dashboard-search">
            <input
              type="text"
              placeholder={`Search ${activeTab === 'users' ? 'users' : activeTab === 'visits' ? 'visit statistics' : activeTab === 'detailed' ? 'detailed visits' : activeTab === 'contact' ? 'contact submissions' : 'products'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="dashboard-tabs">
            <button
              className={`tab ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('users');
                setSearchQuery('');
              }}
            >
              Users ({filteredUsers.length}{searchQuery && ` / ${users.length}`})
            </button>
            <button
              className={`tab ${activeTab === 'visits' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('visits');
                setSearchQuery('');
              }}
            >
              Visit Statistics ({filteredVisits.length}{searchQuery && ` / ${visits.length}`})
            </button>
            <button
              className={`tab ${activeTab === 'detailed' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('detailed');
                setSearchQuery('');
              }}
            >
              Detailed Visits ({filteredDetailedVisits.length}{searchQuery && ` / ${detailedVisits.length}`})
            </button>
            <button
              className={`tab ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('contact');
                setSearchQuery('');
              }}
            >
              Contact Submissions ({filteredContactSubmissions.length}{searchQuery && ` / ${contactSubmissions.length}`})
            </button>
            <button
              className={`tab ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('products');
                setSearchQuery('');
              }}
            >
              Products ({filteredProducts.length}{searchQuery && ` / ${products.length}`})
            </button>
            <button
              className={`tab ${activeTab === 'email' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('email');
                setSearchQuery('');
              }}
            >
              Email Config
            </button>
          </div>

          {activeTab === 'users' && (
            <div className="dashboard-content card">
              <div className="dashboard-header">
                <h2>All Users</h2>
                <button className="btn btn-primary" onClick={handleAddUser}>
                  Add User
                </button>
              </div>
              {/* Desktop Table View */}
              <div className="table-container desktop-view">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Role</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.email}</td>
                        <td>
                          {userPasswords[user.id] ? (
                            <code style={{ 
                              background: '#f3f4f6', 
                              padding: '4px 8px', 
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              color: '#1f2937'
                            }}>
                              {userPasswords[user.id]}
                            </code>
                          ) : (
                            <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>••••••••</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${user.is_admin ? 'badge-admin' : 'badge-user'}`}>
                            {user.is_admin ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td>{new Date(user.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleEditUser(user)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={user.is_admin}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile Card View */}
              <div className="mobile-cards-view">
                {filteredUsers.length === 0 ? (
                  <div className="no-results">
                    <p>No users found matching "{searchQuery}"</p>
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <div key={user.id} className="mobile-card">
                      <div className="mobile-card-header">
                        <div className="mobile-card-title">
                          <span className="mobile-card-label">ID:</span>
                          <span className="mobile-card-value">{user.id}</span>
                        </div>
                        <span className={`badge ${user.is_admin ? 'badge-admin' : 'badge-user'}`}>
                          {user.is_admin ? 'Admin' : 'User'}
                        </span>
                      </div>
                      <div className="mobile-card-body">
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Email:</span>
                          <span className="mobile-card-value">{user.email}</span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Password:</span>
                          <span className="mobile-card-value">
                            {userPasswords[user.id] ? (
                              <code style={{ 
                                background: '#f3f4f6', 
                                padding: '2px 6px', 
                                borderRadius: '3px',
                                fontSize: '0.85rem',
                                color: '#1f2937'
                              }}>
                                {userPasswords[user.id]}
                              </code>
                            ) : (
                              <span style={{ color: '#9ca3af' }}>••••••••</span>
                            )}
                          </span>
                        </div>
                        <div className="mobile-card-row">
                          <span className="mobile-card-label">Created:</span>
                          <span className="mobile-card-value">{new Date(user.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="mobile-card-actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditUser(user)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={user.is_admin}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'visits' && (
            <div className="dashboard-content card">
              <div className="dashboard-header">
                <h2>App Visit Statistics</h2>
              </div>
              {filteredVisits.length === 0 ? (
                <p className="no-data">
                  {searchQuery ? `No visits found matching "${searchQuery}"` : 'No visits recorded yet.'}
                </p>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="table-container desktop-view">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>App URL</th>
                          <th>Total Visits</th>
                          <th>Unique Visitors</th>
                        </tr>
                      </thead>
                    <tbody>
                      {filteredVisits.map((visit, index) => (
                          <tr key={index}>
                            <td>{visit.app_url}</td>
                            <td>{visit.visit_count}</td>
                            <td>{visit.unique_visitors}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile Card View */}
                  <div className="mobile-cards-view">
                    {filteredVisits.map((visit, index) => (
                      <div key={index} className="mobile-card">
                        <div className="mobile-card-body">
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">App URL:</span>
                            <span className="mobile-card-value">{visit.app_url}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Total Visits:</span>
                            <span className="mobile-card-value highlight">{visit.visit_count}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Unique Visitors:</span>
                            <span className="mobile-card-value highlight">{visit.unique_visitors}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'detailed' && (
            <div className="dashboard-content card">
              <div className="dashboard-header">
                <h2>Detailed Visit Records</h2>
              </div>
              {filteredDetailedVisits.length === 0 ? (
                <p className="no-data">
                  {searchQuery ? `No visits found matching "${searchQuery}"` : 'No visits recorded yet.'}
                </p>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="table-container desktop-view">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>App URL</th>
                          <th>User</th>
                          <th>Visited At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                    <tbody>
                      {filteredDetailedVisits.map((visit) => (
                          <tr key={visit.id}>
                            <td>{visit.id}</td>
                            <td>{visit.app_url}</td>
                            <td>{visit.user_email || 'Anonymous'}</td>
                            <td>{new Date(visit.visited_at).toLocaleString()}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => handleEditVisit(visit)}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteVisit(visit.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile Card View */}
                  <div className="mobile-cards-view">
                    {filteredDetailedVisits.map((visit) => (
                      <div key={visit.id} className="mobile-card">
                        <div className="mobile-card-header">
                          <div className="mobile-card-title">
                            <span className="mobile-card-label">ID:</span>
                            <span className="mobile-card-value">{visit.id}</span>
                          </div>
                        </div>
                        <div className="mobile-card-body">
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">App URL:</span>
                            <span className="mobile-card-value">{visit.app_url}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">User:</span>
                            <span className="mobile-card-value">{visit.user_email || 'Anonymous'}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Visited At:</span>
                            <span className="mobile-card-value">{new Date(visit.visited_at).toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="mobile-card-actions">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEditVisit(visit)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteVisit(visit.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'products' && (
            <div className="dashboard-content card">
              <div className="dashboard-header">
                <h2>Products</h2>
                <button className="btn btn-primary" onClick={handleAddProduct}>
                  Add Product
                </button>
              </div>
              {filteredProducts.length === 0 ? (
                <p className="no-data">
                  {searchQuery ? `No products found matching "${searchQuery}"` : 'No products yet. Click "Add Product" to create one.'}
                </p>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="table-container desktop-view">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Status</th>
                          <th>Image</th>
                          <th>URL</th>
                          <th>Created At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.map((product) => (
                          <tr key={product.id}>
                            <td>{product.id}</td>
                            <td>{product.name}</td>
                            <td>
                              <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {product.description || 'No description'}
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${product.status === 'live' ? 'badge-success' : product.status === 'coming soon' ? 'badge-warning' : 'badge-secondary'}`}>
                                {product.status || 'coming soon'}
                              </span>
                            </td>
                            <td>
                              {product.image_url ? (
                                <img src={product.image_url} alt={product.name} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }} />
                              ) : (
                                <span style={{ color: 'var(--text-gray)' }}>No image</span>
                              )}
                            </td>
                            <td>
                              <a href={product.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-blue)', textDecoration: 'none' }}>
                                {product.url.length > 30 ? product.url.substring(0, 30) + '...' : product.url}
                              </a>
                            </td>
                            <td>{new Date(product.created_at).toLocaleDateString()}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => handleEditProduct(product)}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteProduct(product.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile Card View */}
                  <div className="mobile-cards-view">
                    {filteredProducts.map((product) => (
                      <div key={product.id} className="mobile-card">
                        <div className="mobile-card-header">
                          <div className="mobile-card-title">
                            <span className="mobile-card-label">ID:</span>
                            <span className="mobile-card-value">{product.id}</span>
                          </div>
                        </div>
                        <div className="mobile-card-body">
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Name:</span>
                            <span className="mobile-card-value">{product.name}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Status:</span>
                            <span className={`badge ${product.status === 'live' ? 'badge-success' : product.status === 'coming soon' ? 'badge-warning' : 'badge-secondary'}`}>
                              {product.status || 'coming soon'}
                            </span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Description:</span>
                            <span className="mobile-card-value">{product.description || 'No description'}</span>
                          </div>
                          {product.image_url && (
                            <div className="mobile-card-row">
                              <span className="mobile-card-label">Image:</span>
                              <img src={product.image_url} alt={product.name} style={{ width: '100%', maxWidth: '200px', height: 'auto', borderRadius: '4px', marginTop: '4px' }} />
                            </div>
                          )}
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">URL:</span>
                            <a href={product.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-blue)', textDecoration: 'none', wordBreak: 'break-all' }}>
                              {product.url}
                            </a>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Created:</span>
                            <span className="mobile-card-value">{new Date(product.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="mobile-card-actions">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEditProduct(product)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteProduct(product.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="dashboard-content card">
              <div className="dashboard-header">
                <h2>Contact Form Submissions</h2>
              </div>
              {filteredContactSubmissions.length === 0 ? (
                <p className="no-data">
                  {searchQuery ? `No submissions found matching "${searchQuery}"` : 'No contact submissions yet.'}
                </p>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="table-container desktop-view">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Message</th>
                          <th>Email Status</th>
                          <th>User</th>
                          <th>Submitted At</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredContactSubmissions.map((submission) => (
                          <tr key={submission.id}>
                            <td>{submission.id}</td>
                            <td>{submission.name}</td>
                            <td>{submission.email}</td>
                            <td>
                              <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {submission.message}
                              </div>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  setViewingMessage(submission);
                                  setShowMessageModal(true);
                                }}
                                style={{ marginTop: '4px', fontSize: '0.7rem', padding: '2px 8px' }}
                              >
                                View Full
                              </button>
                            </td>
                            <td>
                              {submission.email_status === 'sent' ? (
                                <span className="badge badge-success" title={submission.email_message_id ? `Message ID: ${submission.email_message_id}` : ''}>
                                  ✓ Sent
                                </span>
                              ) : submission.email_status === 'failed' ? (
                                <div>
                                  <span className="badge badge-danger" title={submission.email_error || 'Email failed to send'}>
                                    ✗ Failed
                                  </span>
                                  {submission.email_error && (
                                    <div style={{ 
                                      fontSize: '0.75rem', 
                                      marginTop: '4px', 
                                      color: '#991b1b',
                                      background: '#fee2e2',
                                      padding: '6px 8px',
                                      borderRadius: '4px',
                                      border: '1px solid #fecaca',
                                      maxWidth: '300px',
                                      wordBreak: 'break-word'
                                    }}>
                                      {submission.email_error.length > 100 
                                        ? submission.email_error.substring(0, 100) + '...' 
                                        : submission.email_error}
                                    </div>
                                  )}
                                </div>
                              ) : submission.email_status === 'pending' ? (
                                <span className="badge badge-warning">Pending</span>
                              ) : (
                                <span className="badge badge-secondary">Unknown</span>
                              )}
                              {submission.email_sent_at && (
                                <div style={{ fontSize: '0.75rem', marginTop: '4px', color: 'var(--text-gray)' }}>
                                  {new Date(submission.email_sent_at).toLocaleString()}
                                </div>
                              )}
                            </td>
                            <td>{submission.user_email || 'Anonymous'}</td>
                            <td>{new Date(submission.submitted_at).toLocaleString()}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteContactSubmission(submission.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile Card View */}
                  <div className="mobile-cards-view">
                    {filteredContactSubmissions.map((submission) => (
                      <div key={submission.id} className="mobile-card">
                        <div className="mobile-card-header">
                          <div className="mobile-card-title">
                            <span className="mobile-card-label">ID:</span>
                            <span className="mobile-card-value">{submission.id}</span>
                          </div>
                        </div>
                        <div className="mobile-card-body">
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Name:</span>
                            <span className="mobile-card-value">{submission.name}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Email:</span>
                            <span className="mobile-card-value">{submission.email}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Message:</span>
                            <span className="mobile-card-value" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {submission.message}
                            </span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Email Status:</span>
                            <span className="mobile-card-value">
                              {submission.email_status === 'sent' ? (
                                <span className="badge badge-success">✓ Sent</span>
                              ) : submission.email_status === 'failed' ? (
                                <span className="badge badge-danger">✗ Failed</span>
                              ) : submission.email_status === 'pending' ? (
                                <span className="badge badge-warning">Pending</span>
                              ) : (
                                <span className="badge badge-secondary">Unknown</span>
                              )}
                            </span>
                          </div>
                          {submission.email_error && (
                            <div className="mobile-card-row">
                              <span className="mobile-card-label">Email Error:</span>
                              <span className="mobile-card-value" style={{ 
                                color: '#991b1b', 
                                fontSize: '0.85rem',
                                background: '#fee2e2',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #fecaca',
                                marginTop: '4px'
                              }}>
                                {submission.email_error}
                              </span>
                            </div>
                          )}
                          {submission.email_sent_at && (
                            <div className="mobile-card-row">
                              <span className="mobile-card-label">Email Sent At:</span>
                              <span className="mobile-card-value">{new Date(submission.email_sent_at).toLocaleString()}</span>
                            </div>
                          )}
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">User:</span>
                            <span className="mobile-card-value">{submission.user_email || 'Anonymous'}</span>
                          </div>
                          <div className="mobile-card-row">
                            <span className="mobile-card-label">Submitted At:</span>
                            <span className="mobile-card-value">{new Date(submission.submitted_at).toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="mobile-card-actions">
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteContactSubmission(submission.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'email' && (
            <div className="dashboard-content card">
              <div className="dashboard-header">
                <h2>Email Configuration</h2>
                <button className="btn btn-primary" onClick={handleEditEmailConfig}>
                  {emailConfig ? 'Edit Configuration' : 'Configure Email'}
                </button>
              </div>
              {emailConfig && (emailConfig.email_user || emailConfig.id) ? (
                <div style={{ padding: '20px' }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                    gap: '16px',
                    marginBottom: '24px'
                  }}>
                    <div style={{ 
                      padding: '16px', 
                      background: 'var(--bg-gray)', 
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)'
                    }}>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '4px', fontWeight: 600 }}>
                        Email User (Sender)
                      </div>
                      <div style={{ fontSize: '1rem', color: 'var(--text-dark)', fontWeight: 500 }}>
                        {emailConfig.email_user || 'Not set'}
                      </div>
                    </div>
                    <div style={{ 
                      padding: '16px', 
                      background: 'var(--bg-gray)', 
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)'
                    }}>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '4px', fontWeight: 600 }}>
                        Contact Email (Recipient)
                      </div>
                      <div style={{ fontSize: '1rem', color: 'var(--text-dark)', fontWeight: 500 }}>
                        {emailConfig.contact_email || emailConfig.email_user || 'Not set'}
                      </div>
                    </div>
                    <div style={{ 
                      padding: '16px', 
                      background: 'var(--bg-gray)', 
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)'
                    }}>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '4px', fontWeight: 600 }}>
                        Service Type
                      </div>
                      <div style={{ fontSize: '1rem', color: 'var(--text-dark)', fontWeight: 500 }}>
                        {emailConfig.smtp_host ? 'Custom SMTP' : 'Gmail'}
                      </div>
                    </div>
                    <div style={{ 
                      padding: '16px', 
                      background: 'var(--bg-gray)', 
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)'
                    }}>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '4px', fontWeight: 600 }}>
                        Password Status
                      </div>
                      <div style={{ fontSize: '1rem', color: emailConfig.email_password_encrypted ? '#10b981' : '#dc2626', fontWeight: 500 }}>
                        {emailConfig.email_password_encrypted ? '✓ Configured' : '✗ Not set'}
                      </div>
                    </div>
                  </div>
                  {emailConfig.smtp_host && (
                    <div style={{ 
                      padding: '16px', 
                      background: 'var(--bg-gray)', 
                      borderRadius: '8px',
                      border: '1px solid var(--border-color)',
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '12px', color: 'var(--text-dark)' }}>
                        SMTP Configuration
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                        <div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '4px' }}>SMTP Host</div>
                          <div style={{ fontSize: '0.95rem', color: 'var(--text-dark)', fontWeight: 500 }}>{emailConfig.smtp_host}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '4px' }}>SMTP Port</div>
                          <div style={{ fontSize: '0.95rem', color: 'var(--text-dark)', fontWeight: 500 }}>{emailConfig.smtp_port || 'Not set'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '4px' }}>Secure Connection</div>
                          <div style={{ fontSize: '0.95rem', color: 'var(--text-dark)', fontWeight: 500 }}>
                            {emailConfig.smtp_secure ? 'SSL/TLS' : 'Unsecured / STARTTLS'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {emailConfig.updated_at && (
                    <div style={{ 
                      padding: '12px', 
                      background: 'var(--bg-gray)', 
                      borderRadius: '8px',
                      fontSize: '0.85rem',
                      color: 'var(--text-gray)',
                      marginBottom: '20px'
                    }}>
                      <strong>Last Updated:</strong> {new Date(emailConfig.updated_at).toLocaleString()}
                    </div>
                  )}
                  <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary" onClick={handleTestEmailConfig} disabled={testingEmail}>
                      {testingEmail ? 'Testing...' : 'Send Test Email'}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '20px' }}>
                  <p>Email configuration not set. Click "Configure Email" to set up email notifications for contact form submissions.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* User Edit Modal */}
      {showUserModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content card" onClick={(e) => e.stopPropagation()}>
            <h2>{editingUser.id ? 'Edit User' : 'Add New User'}</h2>
            {error && <div className="form-error">{error}</div>}
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={editingUser.email}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <span style={{ fontSize: '0.9rem', color: 'var(--text-dark)' }}>Admin User</span>
                <div 
                  onClick={() => setEditingUser({ ...editingUser, is_admin: !editingUser.is_admin })}
                  style={{
                    width: '40px',
                    height: '20px',
                    borderRadius: '10px',
                    background: editingUser.is_admin ? '#3b82f6' : '#d1d5db',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                    flexShrink: 0
                  }}
                >
                  <div
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      background: 'white',
                      position: 'absolute',
                      top: '2px',
                      left: editingUser.is_admin ? '22px' : '2px',
                      transition: 'left 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  />
                </div>
              </label>
            </div>
            <div className="form-group">
              <label>Password</label>
              {editingUser.id && userPasswords[editingUser.id] && (
                <div style={{ 
                  marginBottom: '10px', 
                  padding: '8px 12px', 
                  background: '#f3f4f6', 
                  borderRadius: '4px',
                  fontSize: '0.9rem'
                }}>
                  <strong>Current Password:</strong> <code style={{ 
                    background: 'white', 
                    padding: '2px 6px', 
                    borderRadius: '3px',
                    color: '#1f2937'
                  }}>{userPasswords[editingUser.id]}</code>
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={editingUser.id ? "Enter new password to change (min 6 characters, leave blank to keep current)" : "Enter password (min 6 characters)"}
                  value={editingUser.password || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                  style={{ paddingRight: '45px' }}
                  required={!editingUser.id}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
              {editingUser.id && !userPasswords[editingUser.id] && (
                <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-gray)', fontSize: '0.8rem' }}>
                  Note: Passwords are encrypted and cannot be viewed. Enter a new password to change it.
                </small>
              )}
              {!editingUser.id && (
                <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-gray)', fontSize: '0.8rem' }}>
                  Note: The password will be visible in the user list and edit window after creation. Once saved and page is refreshed, it cannot be viewed again for security reasons.
                </small>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowUserModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveUser}>
                {editingUser.id ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visit Edit Modal */}
      {showVisitModal && editingVisit && (
        <div className="modal-overlay" onClick={() => setShowVisitModal(false)}>
          <div className="modal-content card" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Visit</h2>
            {error && <div className="form-error">{error}</div>}
            <div className="form-group">
              <label>App URL</label>
              <input
                type="text"
                value={editingVisit.app_url}
                onChange={(e) => setEditingVisit({ ...editingVisit, app_url: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>User ID (leave empty for anonymous)</label>
              <input
                type="number"
                value={editingVisit.user_id || ''}
                onChange={(e) => setEditingVisit({ ...editingVisit, user_id: e.target.value ? parseInt(e.target.value) : null })}
              />
            </div>
            <div className="form-group">
              <label>Visited At</label>
              <input
                type="datetime-local"
                value={editingVisit.visited_at ? new Date(editingVisit.visited_at).toISOString().slice(0, 16) : ''}
                onChange={(e) => setEditingVisit({ ...editingVisit, visited_at: e.target.value ? new Date(e.target.value).toISOString() : new Date().toISOString() })}
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowVisitModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveVisit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message View Modal */}
      {showMessageModal && viewingMessage && (
        <div className="modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="modal-content card" onClick={(e) => e.stopPropagation()}>
            <h2>Contact Form Message</h2>
            <div className="form-group">
              <label>ID</label>
              <p style={{ margin: 0, padding: '8px 0' }}>{viewingMessage.id}</p>
            </div>
            <div className="form-group">
              <label>Name</label>
              <p style={{ margin: 0, padding: '8px 0' }}>{viewingMessage.name}</p>
            </div>
            <div className="form-group">
              <label>Email</label>
              <p style={{ margin: 0, padding: '8px 0' }}>
                <a href={`mailto:${viewingMessage.email}`}>{viewingMessage.email}</a>
              </p>
            </div>
            <div className="form-group">
              <label>User</label>
              <p style={{ margin: 0, padding: '8px 0' }}>{viewingMessage.user_email || 'Anonymous'}</p>
            </div>
            <div className="form-group">
              <label>Submitted At</label>
              <p style={{ margin: 0, padding: '8px 0' }}>
                {new Date(viewingMessage.submitted_at).toLocaleString()}
              </p>
            </div>
            <div className="form-group">
              <label>Email Status</label>
              <div style={{ marginTop: '8px' }}>
                {viewingMessage.email_status === 'sent' ? (
                  <span className="badge badge-success" style={{ fontSize: '1rem', padding: '8px 12px' }}>
                    ✓ Email Sent Successfully
                  </span>
                ) : viewingMessage.email_status === 'failed' ? (
                  <span className="badge badge-danger" style={{ fontSize: '1rem', padding: '8px 12px' }}>
                    ✗ Email Failed to Send
                  </span>
                ) : viewingMessage.email_status === 'pending' ? (
                  <span className="badge badge-warning" style={{ fontSize: '1rem', padding: '8px 12px' }}>
                    ⏳ Email Pending
                  </span>
                ) : (
                  <span className="badge badge-secondary" style={{ fontSize: '1rem', padding: '8px 12px' }}>
                    Unknown Status
                  </span>
                )}
              </div>
              {viewingMessage.email_message_id && (
                <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: 'var(--text-gray)' }}>
                  Message ID: {viewingMessage.email_message_id}
                </p>
              )}
              {viewingMessage.email_sent_at && (
                <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem', color: 'var(--text-gray)' }}>
                  Sent at: {new Date(viewingMessage.email_sent_at).toLocaleString()}
                </p>
              )}
              {viewingMessage.email_error && (
                <div style={{ 
                  marginTop: '8px', 
                  padding: '12px', 
                  background: '#fee2e2', 
                  borderRadius: '8px',
                  color: '#991b1b',
                  fontSize: '0.9rem',
                  border: '1px solid #fecaca',
                  position: 'relative'
                }}>
                  <strong>Error:</strong> {viewingMessage.email_error}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Message</label>
              <div style={{ 
                padding: '12px', 
                background: 'var(--bg-gray)', 
                borderRadius: '8px', 
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '400px',
                overflowY: 'auto',
                marginTop: '8px'
              }}>
                {viewingMessage.message}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowMessageModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Edit Modal */}
      {showProductModal && editingProduct && (
        <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
          <div className="modal-content card" onClick={(e) => e.stopPropagation()}>
            <h2>{editingProduct.id ? 'Edit Product' : 'Add Product'}</h2>
            {error && <div className="form-error">{error}</div>}
            <div className="form-group">
              <label>Product Name *</label>
              <input
                type="text"
                value={editingProduct.name}
                onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                placeholder="Enter product name"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editingProduct.description || ''}
                onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                placeholder="Enter product description"
                rows="4"
              />
            </div>
            <div className="form-group">
              <label>Image URL</label>
              <input
                type="text"
                value={editingProduct.image_url || ''}
                onChange={(e) => setEditingProduct({ ...editingProduct, image_url: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              {editingProduct.image_url && (
                <img 
                  src={editingProduct.image_url} 
                  alt="Preview" 
                  style={{ 
                    width: '100%', 
                    maxWidth: '200px', 
                    height: 'auto', 
                    marginTop: '8px', 
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>
            <div className="form-group">
              <label>Product URL *</label>
              <input
                type="text"
                value={editingProduct.url}
                onChange={(e) => setEditingProduct({ ...editingProduct, url: e.target.value })}
                placeholder="https://example.com/product"
              />
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-gray)', fontSize: '0.8rem' }}>
                This is the link users will click to visit the product
              </small>
            </div>
            <div className="form-group">
              <label>Status *</label>
              <select
                value={editingProduct.status || 'coming soon'}
                onChange={(e) => setEditingProduct({ ...editingProduct, status: e.target.value })}
              >
                <option value="live">Live</option>
                <option value="coming soon">Coming Soon</option>
                <option value="retired">Retired</option>
              </select>
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-gray)', fontSize: '0.8rem' }}>
                Retired products are hidden from public view but visible in admin dashboard
              </small>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowProductModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveProduct}>
                {editingProduct.id ? 'Save Changes' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Configuration Modal */}
      {showEmailConfigModal && editingEmailConfig && (
        <div className="modal-overlay" onClick={() => setShowEmailConfigModal(false)}>
          <div className="modal-content card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2>Email Configuration</h2>
            {error && (
              <div className="form-error dismissible-error">
                <span>{error}</span>
                <button 
                  className="error-close-btn" 
                  onClick={() => setError('')}
                  aria-label="Close error"
                >
                  ×
                </button>
              </div>
            )}
            {success && (
              <div className="alert alert-success dismissible-alert">
                <span>{success}</span>
                <button 
                  className="alert-close-btn" 
                  onClick={() => setSuccess('')}
                  aria-label="Close success message"
                >
                  ×
                </button>
              </div>
            )}
            <div className="form-group">
              <label>Email User (Sender Email) *</label>
              <input
                type="email"
                value={editingEmailConfig.email_user || ''}
                onChange={(e) => setEditingEmailConfig({ ...editingEmailConfig, email_user: e.target.value })}
                placeholder="your-email@gmail.com"
              />
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-gray)', fontSize: '0.8rem' }}>
                For Gmail, use your Gmail address
              </small>
            </div>
            <div className="form-group">
              <label>
                Email Password / App Password
                {!editingEmailConfig.email_password_encrypted && <span style={{ color: '#dc2626' }}> *</span>}
                {editingEmailConfig.email_password_encrypted && (
                  <span style={{ marginLeft: '8px', fontSize: '0.85rem', color: '#10b981', fontWeight: 'normal' }}>
                    (Configured - optional to change)
                  </span>
                )}
              </label>
              {editingEmailConfig.email_password_encrypted ? (
                <div style={{ 
                  marginBottom: '8px', 
                  padding: '10px 12px', 
                  background: '#f0fdf4', 
                  border: '1px solid #86efac',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  color: '#166534',
                  lineHeight: '1.5'
                }}>
                  <strong>✓ Password is configured</strong><br />
                  The password field is intentionally blank for security. Leave it blank to keep your current password, or enter a new password to update it.
                </div>
              ) : (
                <small style={{ display: 'block', marginBottom: '8px', color: 'var(--text-gray)', fontSize: '0.8rem' }}>
                  For Gmail, use an App Password. See the documentation for instructions.
                </small>
              )}
              <div style={{ position: 'relative' }}>
                <input
                  type={showEmailPassword ? 'text' : 'password'}
                  value={editingEmailConfig.email_password || ''}
                  onChange={(e) => setEditingEmailConfig({ ...editingEmailConfig, email_password: e.target.value })}
                  placeholder={
                    editingEmailConfig.email_password_encrypted
                      ? 'Leave blank to keep current password, or enter new password'
                      : 'Enter email password or app password'
                  }
                  style={{ paddingRight: '45px' }}
                  required={!editingEmailConfig.email_password_encrypted}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowEmailPassword(!showEmailPassword)}
                  aria-label={showEmailPassword ? 'Hide password' : 'Show password'}
                  title={showEmailPassword ? 'Hide password' : 'Show password'}
                >
                  {showEmailPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-gray)', fontSize: '0.8rem' }}>
                For Gmail, use an App Password (not your regular password). Generate one at{' '}
                <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">
                  https://myaccount.google.com/apppasswords
                </a>
              </small>
            </div>
            <div className="form-group">
              <label>Contact Email (Recipient) *</label>
              <input
                type="email"
                value={editingEmailConfig.contact_email || ''}
                onChange={(e) => setEditingEmailConfig({ ...editingEmailConfig, contact_email: e.target.value })}
                placeholder="contact@example.com"
              />
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-gray)', fontSize: '0.8rem' }}>
                Email address where contact form submissions will be sent
              </small>
            </div>
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.95rem' }}>
                Email Service Type
              </label>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
                <label className={`radio-option ${!editingEmailConfig.use_custom_smtp ? 'checked' : ''}`}>
                  <input
                    type="radio"
                    name="email-service"
                    value="gmail"
                    checked={!editingEmailConfig.use_custom_smtp}
                    onChange={() => {
                      setEditingEmailConfig({ 
                        ...editingEmailConfig, 
                        use_custom_smtp: false,
                        smtp_host: null,
                        smtp_port: null,
                        smtp_secure: false
                      });
                    }}
                  />
                  <span>Gmail (Default)</span>
                </label>
                <label className={`radio-option ${editingEmailConfig.use_custom_smtp ? 'checked' : ''}`}>
                  <input
                    type="radio"
                    name="email-service"
                    value="custom"
                    checked={editingEmailConfig.use_custom_smtp || false}
                    onChange={() => {
                      setEditingEmailConfig({ 
                        ...editingEmailConfig, 
                        use_custom_smtp: true,
                        smtp_host: editingEmailConfig.smtp_host || '',
                        smtp_port: editingEmailConfig.smtp_port || 587,
                        smtp_secure: editingEmailConfig.smtp_secure || false
                      });
                    }}
                  />
                  <span>Custom SMTP</span>
                </label>
              </div>
              <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-gray)', fontSize: '0.8rem' }}>
                Choose Gmail for easy setup with App Password, or Custom SMTP for other email providers
              </small>
            </div>
            {editingEmailConfig.use_custom_smtp && (
              <>
                <div className="form-group">
                  <label>SMTP Host *</label>
                  <input
                    type="text"
                    value={editingEmailConfig.smtp_host || ''}
                    onChange={(e) => setEditingEmailConfig({ ...editingEmailConfig, smtp_host: e.target.value })}
                    placeholder="smtp.example.com"
                  />
                </div>
                <div className="form-group">
                  <label>SMTP Port *</label>
                  <input
                    type="number"
                    value={editingEmailConfig.smtp_port || ''}
                    onChange={(e) => setEditingEmailConfig({ ...editingEmailConfig, smtp_port: parseInt(e.target.value) || '' })}
                    placeholder="587"
                  />
                  <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-gray)', fontSize: '0.8rem' }}>
                    Common ports: 587 (TLS), 465 (SSL), 25 (unsecured)
                  </small>
                </div>
                <div className="form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: 'var(--text-dark)', fontSize: '0.95rem' }}>
                    Connection Security
                  </label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <label className={`radio-option ${!editingEmailConfig.smtp_secure ? 'checked' : ''}`}>
                      <input
                        type="radio"
                        name="smtp-secure"
                        value="false"
                        checked={!editingEmailConfig.smtp_secure}
                        onChange={() => setEditingEmailConfig({ ...editingEmailConfig, smtp_secure: false })}
                      />
                      <span>Unsecured / STARTTLS</span>
                    </label>
                    <label className={`radio-option ${editingEmailConfig.smtp_secure ? 'checked' : ''}`}>
                      <input
                        type="radio"
                        name="smtp-secure"
                        value="true"
                        checked={editingEmailConfig.smtp_secure || false}
                        onChange={() => setEditingEmailConfig({ ...editingEmailConfig, smtp_secure: true })}
                      />
                      <span>SSL/TLS</span>
                    </label>
                  </div>
                </div>
              </>
            )}
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowEmailConfigModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveEmailConfig}>
                Save Configuration
              </button>
              <button className="btn btn-secondary" onClick={handleTestEmailConfig} disabled={testingEmail}>
                {testingEmail ? 'Testing...' : 'Test Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
